/**
 * Button — the four variants from the §13 mockup, nothing else.
 *
 * `variant`: 'primary' | 'secondary' | 'tertiary' | 'danger'
 *   primary   — one per view (§5). Solid --color-blue.
 *   secondary — outlined in --color-blue, for the second-most-wanted action.
 *   tertiary  — neutral outline, for everything else.
 *   danger    — --color-err. Every destructive action uses this (§3), never
 *               the logo red.
 *
 * `icon` takes a lucide component (not an element) and renders it leading.
 * Anything else (type, onClick, title, form, …) passes through to <button>.
 *
 * `as` renders a different element with the same skin — pass `as={Link}` (+ `to`)
 * for a navigation action, or `as="a"` (+ `href`) for an external one. Nesting a
 * <button> inside a <Link> instead would give two focusable controls for one
 * action, which is worse for keyboard users than it looks.
 */

// `sm` exists for dense action clusters — the Servers row carries up to eight
// controls in one cell and the default size overflows it. Not a licence to
// shrink buttons elsewhere: default is the size the mockup shows.
const SIZES = {
  md: 'px-3.5 py-2 text-[13px] gap-[7px]',
  sm: 'px-2 py-1 text-[12px] gap-1.5',
};

const VARIANTS = {
  primary:
    'bg-blue text-white border-transparent hover:bg-blue-deep',
  secondary:
    'bg-white text-blue border-blue hover:bg-s-blue',
  tertiary:
    'bg-white text-t1 border-bd-strong hover:bg-s-3',
  danger:
    'bg-white text-err border-err-bd hover:bg-err-bg',
};

export function Button({
  variant = 'tertiary',
  size = 'md',
  icon: Icon,
  children,
  className = '',
  disabled = false,
  type = 'button',
  as: Component = 'button',
  ...rest
}) {
  const isButton = Component === 'button';
  return (
    <Component
      // `type` and `disabled` are button-only; a Link would render them as
      // stray DOM attributes.
      type={isButton ? type : undefined}
      disabled={isButton ? disabled : undefined}
      aria-disabled={!isButton && disabled ? 'true' : undefined}
      className={[
        'inline-flex items-center border rounded-ctl font-medium leading-none',
        'transition-colors whitespace-nowrap',
        SIZES[size] ?? SIZES.md,
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        VARIANTS[variant] ?? VARIANTS.tertiary,
        className,
      ].join(' ')}
      {...rest}
    >
      {Icon && (
        <Icon
          className={[size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5', 'shrink-0'].join(' ')}
          aria-hidden="true"
        />
      )}
      {children}
    </Component>
  );
}
